import { useEffect, useState } from 'react'
import { NavLink, Route, Routes, useLocation } from 'react-router-dom'
import {
  Speedometer2, ArrowLeftRight, Wallet2, CalendarCheck, Bank, GraphUpArrow, Bag, Gear, BoxArrowRight, List, Key, Plug, Sun, Moon, CircleHalf,
} from 'react-bootstrap-icons'
import { useStore } from './store.jsx'
import { logOut, LOCAL } from './lib/firebase.js'
import { getTheme, setTheme } from './lib/theme.js'
import Login from './pages/Login.jsx'
import Dashboard from './pages/Dashboard.jsx'
import Transactions from './pages/Transactions.jsx'
import Accounts from './pages/Accounts.jsx'
import Recurring from './pages/Recurring.jsx'
import Loans from './pages/Loans.jsx'
import Investments from './pages/Investments.jsx'
import Wishlist from './pages/Wishlist.jsx'
import Settings from './pages/Settings.jsx'
import Passwords from './pages/Passwords.jsx'
import Plugs from './pages/Plugs.jsx'
import { useDailySnapshot } from './lib/useDailySnapshot.js'

const NAV = [
  { to: '/', label: 'Dashboard', icon: Speedometer2, end: true },
  { to: '/transactions', label: 'Income & outcome', icon: ArrowLeftRight },
  { to: '/accounts', label: 'Balances', icon: Wallet2 },
  { to: '/recurring', label: 'Bills & recurring', icon: CalendarCheck },
  { to: '/loans', label: 'Loans', icon: Bank },
  { to: '/investments', label: 'Lightyear & Coinbase', icon: GraphUpArrow },
  { to: '/wishlist', label: 'Planned purchases', icon: Bag },
  { to: '/passwords', label: 'Passwords', icon: Key },
  { to: '/plugs', label: 'Smart plugs', icon: Plug },
  { to: '/settings', label: 'Settings', icon: Gear },
]

export default function App() {
  const { user, data, error } = useStore()
  const [open, setOpen] = useState(false)
  const location = useLocation()
  useEffect(() => setOpen(false), [location.pathname])
  useDailySnapshot(data)

  if (user === undefined) return <FullscreenSpinner />
  if (!user) return <Login />

  return (
    <div className="app-shell">
      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="brand">Money</div>
        <nav>
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className="nav-item">
              <Icon /> <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <ThemeSwitch />
          {LOCAL ? (
            <div className="small text-body-secondary">Local mode: data is saved in this browser. Export a backup in Settings.</div>
          ) : (
            <>
              <div className="small text-body-secondary text-truncate" title={user.email}>{user.email}</div>
              <button className="btn btn-sm btn-outline-secondary w-100" onClick={logOut}><BoxArrowRight /> Sign out</button>
            </>
          )}
        </div>
      </aside>
      {open && <div className="sidebar-backdrop" onClick={() => setOpen(false)} />}
      <main className="content">
        <button className="btn btn-outline-secondary btn-sm d-lg-none mb-3" onClick={() => setOpen(true)} aria-label="Menu"><List /> Menu</button>
        {data ? (
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/transactions" element={<Transactions />} />
            <Route path="/accounts" element={<Accounts />} />
            <Route path="/recurring" element={<Recurring />} />
            <Route path="/loans" element={<Loans />} />
            <Route path="/investments" element={<Investments />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/passwords" element={<Passwords />} />
            <Route path="/plugs" element={<Plugs />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        ) : error ? (
          <div className="alert alert-danger">
            <div className="fw-semibold">Could not load your data</div>
            <div className="small">{error}</div>
            <button className="btn btn-sm btn-outline-danger mt-2" onClick={logOut}>Sign out</button>
          </div>
        ) : <FullscreenSpinner />}
      </main>
    </div>
  )
}

const THEMES = [{ id: 'light', label: 'Light', icon: Sun }, { id: 'auto', label: 'Auto', icon: CircleHalf }, { id: 'dark', label: 'Dark', icon: Moon }]

function ThemeSwitch() {
  const [theme, set] = useState(getTheme)
  return (
    <div className="theme-switch" role="radiogroup" aria-label="Colour theme">
      {THEMES.map(({ id, label, icon: Icon }) => (
        <button key={id} type="button" role="radio" aria-checked={theme === id} className={theme === id ? 'active' : ''}
          onClick={() => { setTheme(id); set(id) }}><Icon /> {label}</button>
      ))}
    </div>
  )
}

function FullscreenSpinner() {
  return <div className="d-flex justify-content-center align-items-center py-5"><div className="spinner-border text-primary" /></div>
}
